import { ACTION_TYPE, Action, ActionPersistData, ElementToUpdate, OpUpdateValueConverter, Operation, OperationType } from "../action.model";
import { actualizeFieldValueIfNeeded } from "../services/cache.tools";

/**
 * Get existing element to update or create a new one
 * @param actionPersistenceData 
 * @returns {ElementToUpdate}
 */
export function getOrCreateElementToUpdate<Context>(actionPersistenceData: ActionPersistData<Context>, elementTypeName: string, elementId: number, elementsToUpdateByTypeAndId: Record<string, ElementToUpdate>): ElementToUpdate {
    if (!actionPersistenceData.elementsToUpdate) {
        actionPersistenceData.elementsToUpdate = [];
    }
    const opKey = elementTypeName + "@" + elementId;
    const opFromRecord = elementsToUpdateByTypeAndId[opKey];
    if (opFromRecord != null) {
        // element already has an ElementToUpdate => the update will be added into it
        return opFromRecord;
    }
    // element does not have any ElementToUpdate => create one
    const operation: ElementToUpdate = {
        elementTypeName,
        elementId,
        fieldsToUpdate: {},
        newValuesDisplayString: {},
        oldValuesDisplayString: {}
    };
    actionPersistenceData.elementsToUpdate.push(operation);
    elementsToUpdateByTypeAndId[opKey] = operation;
    return operation;
}

export class OpUpdate<ElementTypes, TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName], K extends keyof ElementType, V extends ElementType[K], AppLogInfos, Context> implements Operation<ElementTypes, AppLogInfos, Context> {

    protected _oldValue: V;

    constructor(protected readonly _elementTypeName: TypeName, protected _elementToUpdate: ElementType, protected readonly _field: K, protected _newValue: V, protected _opMessageConverter: OpUpdateValueConverter<AppLogInfos, Context, ElementTypes, TypeName, ElementType, K>) {
        this._oldValue = this._elementToUpdate[this._field] as V;
    }

    /** @inheritdoc */
    public getElement(): ElementType {
        return this._elementToUpdate;
    }

    /**
     * Get _field
     */
    public getField(): K {
        return this._field;
    }

    /**
     * Get _newValue
     */
    public getNewValue(): V {
        return this._newValue;
    }

    /**
     * Get _oldValue
     */
    public getOldValue(): V {
        return this._oldValue;
    }

    /** @inheritdoc */
    public get elementTypeName(): keyof ElementTypes { return this._elementTypeName; }

    /**
     * Overwrite the newValue and update the object.
     * This function can be used when several operations update the same field on one object and we don't want to create a new operation.
     * @param newValue 
     */
    public updateNewValue(newValue: V): void {
        this._newValue = newValue;
        this._updateElement(newValue);
    }

    /**
     * Does this._objectToUpdate[this._field] = value;
     */
    protected _updateElement(value: V): void {
        this._elementToUpdate[this._field] = value;
    }

    /**
     * set _newValue
     */
    public process(action: Action<ElementTypes, AppLogInfos, Context>): void {
        this._oldValue = this._elementToUpdate[this._field] as V;// store the current value since the object may have been modified in a persistence synchronization
        this._updateElement(this._newValue);
    }

    /**
     * restore _oldValue
     */
    public undo(action: Action<ElementTypes, AppLogInfos, Context>): void {
        this._newValue = this._elementToUpdate[this._field] as V;// store the current value since the object may have been modified in a persistence synchronization
        this._updateElement(this._oldValue);
    }

    /**
     * Returns the type of the current Operation
     * @returns {OperationType}
     */
    public getOperationType(): OperationType {
        return OperationType.UPDATE;
    }

    /**
     * Action to string
     * @param action 
     * @returns {string}
     */
    public toString(action: Action<ElementTypes, AppLogInfos, Context>): string {
        return `Update ${String(this._elementTypeName)}.${String(this._field)}: ${this._oldValue} => ${this._newValue}`;
    }

    /**
     * update internal objects (retrieve the element from the cache or update ids and foreign keys)
     */
    public actualize(action: Action<ElementTypes, AppLogInfos, Context>): void {
        const cache = action.getCacheManager();
        const cacheRepository = cache.getDTOCacheRepository(this._elementTypeName);
        this._elementToUpdate = cacheRepository.getUpToDateElement(this._elementToUpdate) as ElementType;

        // FOREIGN KEY MANAGEMENT
        const foreignTable = cache.getForeignKeyTableName(this._elementTypeName, this._field as keyof ElementTypes[TypeName]);
        if (foreignTable !== null) {
            // The foreign key may have been updated by a previous action, so here we make sure the ids are up to date.
            // The DTO is also updated so next operations will have the correct id, because actualize is not called before process.
            const dtoToUpdate = {
                dto: this._elementToUpdate,
                field: this._field as string
            };
            this._newValue = actualizeFieldValueIfNeeded(cache, foreignTable, this._newValue, dtoToUpdate);
            this._oldValue = actualizeFieldValueIfNeeded(cache, foreignTable, this._oldValue, dtoToUpdate);
        }
    }

    /**
     * Add server action element to ActionPersistenceData
     */
    public addServerActionElement(actionPersistenceData: ActionPersistData<Context>, params: { elementsToUpdateByTypeAndId: Record<string, ElementToUpdate>, action: Action<ElementTypes, AppLogInfos, Context>, actionType: ACTION_TYPE }): void {
        const cacheRepository = params.action.getCacheManager().getDTOCacheRepository(this._elementTypeName);
        const elementId = cacheRepository.getId(this._elementToUpdate);
        const elementsToUpdate = getOrCreateElementToUpdate(actionPersistenceData, this._elementTypeName as string, elementId, params.elementsToUpdateByTypeAndId);

        const { oldValue, newValue } = params.actionType === ACTION_TYPE.UNDO ? { oldValue: this._newValue, newValue: this._oldValue } : { oldValue: this._oldValue, newValue: this._newValue };
        // register value to update
        elementsToUpdate.fieldsToUpdate[this._field as string] = newValue ?? null;// undefined => null

        const updateMessages = this._opMessageConverter(params.action, elementId, this._field, oldValue, newValue);
        // keep only first oldValue
        if (elementsToUpdate.oldValuesDisplayString[String(this._field)] == null) { elementsToUpdate.oldValuesDisplayString[String(this._field)] = updateMessages.oldValue; }
        // overwrite with last newValue
        elementsToUpdate.newValuesDisplayString[String(this._field)] = updateMessages.newValue;
    }
}